import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useQuery } from "@apollo/client/react";
import { toast } from "@/components/feedback/toast";
import { AVAILABLE_PROVIDERS, ME, MY_CONNECTIONS, SESSION_CONTEXT } from "@/graphql/queries";
import {
  clearClientCredentialObservations,
  getClientCredentialObservationsSnapshot,
  observeClientCredentialRecovery,
  retainClientCredentialObservations,
  subscribeClientCredentialObservations,
  type ClientCredentialObservation,
} from "@/lib/clientCredentialObservations";
import {
  clearDefaultConnectionIdForUser,
  getActiveConnectionId,
  getDefaultConnectionIdForUser,
  setActiveConnectionId,
  setDefaultConnectionIdForUser,
} from "@/lib/connectionSeed";
import {
  PROVIDER_REGISTRY,
  type ProviderDefinition,
  type ProviderId,
} from "@/lib/providerRegistry";
import { providerFromProjectPath } from "@/lib/authRedirect";
import { LEGAL_VERSIONS } from "@shared/legalVersions";
import { isFreshPendingAuthResult, PENDING_AUTH_RESULT_KEY } from "@/lib/authState";
import { recordAuthTrace } from "@/lib/authTrace";

export type { ProviderDefinition, ProviderId };

/** True when the current URL is the demo entry or a `fixtures/...` project. */
function urlIndicatesFixtures(): boolean {
  if (typeof window === "undefined") return false;
  if (window.location.pathname === "/demo") return true;
  const project = new URLSearchParams(window.location.search).get("project");
  return providerFromProjectPath(project) === "fixtures";
}

// Fixture mode is derived, never persisted: the demo entry (/demo) and
// fixtures project paths are both visible in the URL, so a reload re-derives
// the mode from the URL alone. (The old sessionStorage "active provider" flag
// could go stale and trap a real authenticated session in demo mode.)

export type VerifiedEmail = {
  email: string;
  providers: string[];
  isPrimary: boolean;
};

export type AccountUser = {
  id: string;
  displayName: string | null;
  primaryEmail: string | null;
  avatarUrl: string | null;
  verifiedEmails: VerifiedEmail[];
  firstRunDismissedAt?: string | null;
  /** ISO timestamp the post-signup contact-prefs modal was resolved; null = never asked. */
  contactPrefsAcknowledgedAt?: string | null;
  /** ISO timestamp the user opted in to marketing email; null = no consent. */
  marketingConsentAt?: string | null;
  termsAcceptedAt?: string | null;
  termsAcceptedVersion?: string | null;
  privacyAcknowledgedAt?: string | null;
  privacyAcknowledgedVersion?: string | null;
};

export type ConnectionSummary = {
  id: string;
  provider: ProviderId;
  instanceUrl: string;
  providerUserId: string;
  login: string | null;
  profileName: string | null;
  avatarUrl: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  verifiedEmails: string[];
  status: string;
  userActionRequired: string;
};

export type ViewContext =
  | {
      kind: "connection";
      connectionId: string;
      provider: ProviderId;
      instanceUrl: string;
      login: string | null;
      profileName: string | null;
      projectPath: string | null;
    }
  | {
      kind: "fixtures";
      projectPath: string | null;
    }
  | {
      kind: "none";
    };

export type SessionState = {
  user: AccountUser | null;
  connections: ConnectionSummary[];
  viewContext: ViewContext;
  activeConnectionId: string | null;
  activeConnection: ConnectionSummary | null;
  provider: ProviderId | null;
  connectedProviders: ProviderId[];
  instanceUrl: string;
  isAuthenticated: boolean;
  // True ONLY for a real signed-in session — NOT inflated by fixtures/demo or the local
  // test connection (unlike isAuthenticated). Use this to decide "does this visitor have
  // an account" so demo/fixtures visitors aren't mislabeled as signed-in (P-199).
  hasRealSession: boolean;
  isLoading: boolean;
  needsReconnect: boolean;
  clientCredentialObservations: readonly ClientCredentialObservation[];
  availableProviders: ProviderDefinition[];
  providersLoading: boolean;
  debugAccess: {
    enabled: boolean;
    authDebug: boolean;
    ddbCacheDebug: boolean;
    comparePerformanceDebug: boolean;
    compareStreamDebug: boolean;
    designSystem: boolean;
    developerTools: boolean;
  };
  hasConnections: boolean;
  isZeroConnectionState: boolean;
  accountLoadError: boolean;
  connectionStatus: string;
  connectionUserAction: string;
};

export type SessionActions = {
  switchViewConnection: (connectionId: string) => void;
  switchFixtureView: () => void;
  switchProvider: (provider: ProviderId) => void;
  switchConnection: (connectionId: string) => void;
  clearActiveProvider: () => void;
  clearActiveConnection: () => void;
  refetchSession: () => Promise<void>;
};

export type UseConnectionStoreReturn = { state: SessionState; actions: SessionActions };

type UseConnectionStoreInput = {
  initialProvider?: ProviderId;
};

type PendingOAuthConnection = { provider: ProviderId; connectionId: string } | null;

/**
 * Resolve the active connection without crossing an OAuth handoff boundary.
 *
 * A fresh callback names the only connection that may serve the redirected
 * project. If MY_CONNECTIONS has not incorporated that connection yet, return
 * null and let the app hold protected queries instead of falling through to a
 * stale tab/default/session connection from the previous provider.
 */
export function resolveActiveConnection(input: {
  connections: ConnectionSummary[];
  pendingOAuthResult: PendingOAuthConnection;
  selectedConnectionId: string | null;
  defaultConnectionId: string | null;
  sessionProvider: string | null | undefined;
}): ConnectionSummary | null {
  const byId = (id: string | null | undefined) =>
    (id && input.connections.find((connection) => connection.id === id)) || null;

  if (input.pendingOAuthResult) {
    const pending = byId(input.pendingOAuthResult.connectionId);
    return pending?.provider === input.pendingOAuthResult.provider ? pending : null;
  }

  return (
    byId(input.selectedConnectionId) ??
    byId(input.defaultConnectionId) ??
    input.connections.find((connection) => connection.provider === input.sessionProvider) ??
    input.connections[0] ??
    null
  );
}

type SessionContextData = {
  sessionContext: {
    authenticated: boolean;
    provider: string | null;
    instanceUrl: string | null;
    debugAccess: {
      enabled: boolean;
      authDebug: boolean;
      ddbCacheDebug: boolean;
      comparePerformanceDebug: boolean;
      compareStreamDebug: boolean;
      designSystem: boolean;
      developerTools: boolean;
    };
  };
};

type MeData = {
  me: {
    id: string;
    displayName: string | null;
    primaryEmail: string | null;
    avatarUrl: string | null;
    firstRunDismissedAt: string | null;
    contactPrefsAcknowledgedAt: string | null;
    marketingConsentAt: string | null;
    termsAcceptedAt: string | null;
    termsAcceptedVersion: string | null;
    privacyAcknowledgedAt: string | null;
    privacyAcknowledgedVersion: string | null;
    verifiedEmails: VerifiedEmail[];
  };
};

type MyConnectionsData = {
  myConnections: Array<{
    id: string;
    provider: string;
    instanceUrl: string;
    providerUserId: string;
    login: string | null;
    profileName: string | null;
    avatarUrl: string | null;
    createdAt: string | null;
    updatedAt: string | null;
    verifiedEmails: string[];
    status: string;
    userActionRequired: string;
  }>;
};

function normalizeConnections(
  data: MyConnectionsData["myConnections"] | undefined
): ConnectionSummary[] {
  return (data ?? [])
    .filter(
      (item): item is MyConnectionsData["myConnections"][number] & { provider: ProviderId } =>
        item.provider in PROVIDER_REGISTRY
    )
    .map((item) => ({
      id: item.id,
      provider: item.provider as ProviderId,
      instanceUrl: item.instanceUrl,
      providerUserId: item.providerUserId,
      login: item.login ?? null,
      profileName: item.profileName ?? null,
      avatarUrl: item.avatarUrl ?? null,
      createdAt: item.createdAt ?? null,
      updatedAt: item.updatedAt ?? null,
      verifiedEmails: Array.isArray(item.verifiedEmails) ? item.verifiedEmails : [],
      status: typeof item.status === "string" ? item.status : "ready",
      userActionRequired:
        typeof item.userActionRequired === "string" ? item.userActionRequired : "none",
    }));
}

function readPendingOAuthResult(): { provider: ProviderId; connectionId: string } | null {
  if (typeof window === "undefined") return null;
  const url = new URL(window.location.href);
  if (url.searchParams.get("oauth") !== "complete") return null;
  if (!isFreshPendingAuthResult()) {
    recordAuthTrace("connection.pending_oauth.drop_stale_initial", {
      search: window.location.search,
    });
    sessionStorage.removeItem(PENDING_AUTH_RESULT_KEY);
    return null;
  }
  const raw = sessionStorage.getItem(PENDING_AUTH_RESULT_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as {
      provider?: unknown;
      connectionId?: unknown;
    };
    if (
      typeof parsed.provider === "string" &&
      parsed.provider in PROVIDER_REGISTRY &&
      typeof parsed.connectionId === "string" &&
      parsed.connectionId.trim().length > 0
    ) {
      return {
        provider: parsed.provider as ProviderId,
        connectionId: parsed.connectionId,
      };
    }
  } catch {
    return null;
  }
  return null;
}

function isLocalBrowserHost(): boolean {
  if (typeof window === "undefined") return false;
  return ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
}

function readTestConnection(): {
  id: string;
  provider: ProviderId;
  instanceUrl: string;
  accessToken?: string;
} | null {
  if (typeof window === "undefined") return null;
  if (import.meta.env.MODE !== "test" && !isLocalBrowserHost()) return null;
  const raw = sessionStorage.getItem("__deploycompare_test_connection");
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as {
      id?: unknown;
      provider?: unknown;
      instanceUrl?: unknown;
      accessToken?: unknown;
    };
    if (
      typeof parsed.id === "string" &&
      typeof parsed.provider === "string" &&
      parsed.provider in PROVIDER_REGISTRY &&
      typeof parsed.instanceUrl === "string"
    ) {
      return {
        id: parsed.id,
        provider: parsed.provider as ProviderId,
        instanceUrl: parsed.instanceUrl,
        accessToken: typeof parsed.accessToken === "string" ? parsed.accessToken : undefined,
      };
    }
  } catch {
    return null;
  }
  return null;
}

export function useConnectionStore({
  initialProvider,
}: UseConnectionStoreInput = {}): UseConnectionStoreReturn {
  const clientCredentialObservations = useSyncExternalStore(
    subscribeClientCredentialObservations,
    getClientCredentialObservationsSnapshot,
    getClientCredentialObservationsSnapshot
  );
  const [fixtureMode, setFixtureMode] = useState<boolean>(
    () =>
      initialProvider === "fixtures" || (typeof window !== "undefined" && urlIndicatesFixtures())
  );
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(
    typeof window !== "undefined" ? getActiveConnectionId() : null
  );
  const [pendingOAuthResult, setPendingOAuthResult] = useState(readPendingOAuthResult);
  const [oauthCompletionInFlight, setOauthCompletionInFlight] = useState(false);

  const {
    data: sessionData,
    loading: sessionLoading,
    refetch: refetchSessionContext,
  } = useQuery<SessionContextData>(SESSION_CONTEXT, {
    context: { operationKind: "account" },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

  const session = sessionData?.sessionContext;
  const isFixtureMode = fixtureMode;
  const isSessionAuthenticated = session?.authenticated === true;
  const testConnection = useMemo(() => readTestConnection(), []);
  const useTestConnection = Boolean(
    testConnection && !isFixtureMode && (isSessionAuthenticated || isLocalBrowserHost())
  );

  const {
    data: meData,
    loading: meLoading,
    error: meError,
    refetch: refetchMe,
  } = useQuery<MeData>(ME, {
    skip: !isSessionAuthenticated || isFixtureMode || useTestConnection,
    context: { operationKind: "account" },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

  const {
    data: connectionsData,
    loading: connectionsLoading,
    error: connectionsError,
    refetch: refetchConnections,
  } = useQuery<MyConnectionsData>(MY_CONNECTIONS, {
    skip: !isSessionAuthenticated || isFixtureMode || useTestConnection,
    context: { operationKind: "account" },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

  const { data: providersData, loading: providersLoading } = useQuery<{
    availableProviders: string[];
  }>(AVAILABLE_PROVIDERS, {
    context: { operationKind: "account" },
  });

  const availableProviders = useMemo(() => {
    // The demo is an isolated fixtures view, not a view of the signed-in
    // account's provider catalog. Keeping this list route-scoped prevents
    // GitHub/GitLab entries from leaking into /demo, and deduping protects
    // the selector from duplicate backend values on /app.
    if (isFixtureMode) return [PROVIDER_REGISTRY.fixtures];
    const ids = new Set<ProviderId>();
    for (const id of providersData?.availableProviders ?? []) {
      // `fixtures` is an internal/demo data source whose display label is
      // intentionally GitLab-like. It is not a connectable account provider
      // and must never appear beside real GitLab in the /app selector.
      if (id !== "fixtures" && id in PROVIDER_REGISTRY) ids.add(id as ProviderId);
    }
    return [...ids].map((id) => PROVIDER_REGISTRY[id]);
  }, [isFixtureMode, providersData]);

  // Memoized so effects depending on `user` keep a stable reference
  // (the test-connection branch builds a fresh object literal per render).
  const user = useMemo(
    () =>
      isFixtureMode
        ? null
        : useTestConnection
          ? {
              id: "test-user",
              displayName: "Test User",
              primaryEmail: "test@example.com",
              avatarUrl: null,
              firstRunDismissedAt: null,
              // Established/onboarded user: contact prefs already acknowledged, so
              // the post-signup ContactPrefsModal stays closed in app integration
              // tests (its own behavior is covered by ContactPrefsModal.test.tsx).
              contactPrefsAcknowledgedAt: "2026-01-01T00:00:00.000Z",
              marketingConsentAt: null,
              // Track the CURRENT required versions so a legal-version bump
              // can't silently reopen the modal for this synthetic user.
              termsAcceptedAt: `${LEGAL_VERSIONS.terms}T00:00:00.000Z`,
              termsAcceptedVersion: LEGAL_VERSIONS.terms,
              privacyAcknowledgedAt: `${LEGAL_VERSIONS.privacy}T00:00:00.000Z`,
              privacyAcknowledgedVersion: LEGAL_VERSIONS.privacy,
              verifiedEmails: [
                {
                  email: "test@example.com",
                  providers: [testConnection!.provider],
                  isPrimary: true,
                },
              ],
            }
          : (meData?.me ?? null),
    [isFixtureMode, useTestConnection, testConnection, meData?.me]
  );
  const userId = user?.id ?? null;
  const connections = useMemo(
    () =>
      isFixtureMode
        ? []
        : testConnection && useTestConnection
          ? [
              {
                id: testConnection.id,
                provider: testConnection.provider,
                instanceUrl: testConnection.instanceUrl,
                providerUserId: "test-provider-user",
                login: "test-user",
                profileName: "Test User",
                avatarUrl: null,
                createdAt: null,
                updatedAt: null,
                verifiedEmails: ["test@example.com"],
                status: "ready",
                userActionRequired: "none",
              },
            ]
          : normalizeConnections(connectionsData?.myConnections),
    [connectionsData?.myConnections, isFixtureMode, testConnection, useTestConnection]
  );

  // Pure, validated resolution — every candidate must exist in the LIVE
  // connection list, so a stale persisted id can only fall through, never
  // override the session. Provider is derived from the resolved connection
  // (never independent state), so the filter, the picker, and the query
  // identity cannot disagree.
  const activeConnection = useMemo(() => {
    if (isFixtureMode) return null;
    if (connections.length === 0) return null;
    const defaultConnectionId =
      typeof window !== "undefined" && userId ? getDefaultConnectionIdForUser(userId) : null;

    return resolveActiveConnection({
      connections,
      pendingOAuthResult,
      selectedConnectionId,
      defaultConnectionId,
      sessionProvider: session?.provider,
    });
  }, [
    connections,
    isFixtureMode,
    pendingOAuthResult,
    selectedConnectionId,
    session?.provider,
    userId,
  ]);

  const activeConnectionId = activeConnection?.id ?? null;
  const activeClientCredentialObservation = activeConnectionId
    ? (clientCredentialObservations.find(
        (observation) => observation.connectionId === activeConnectionId
      ) ?? null)
    : null;
  const needsReconnect = activeClientCredentialObservation?.state === "reconnect_required";
  const provider: ProviderId | null = isFixtureMode
    ? "fixtures"
    : (activeConnection?.provider ?? null);
  const viewContext: ViewContext = isFixtureMode
    ? { kind: "fixtures", projectPath: null }
    : activeConnection
      ? {
          kind: "connection",
          connectionId: activeConnection.id,
          provider: activeConnection.provider,
          instanceUrl: activeConnection.instanceUrl,
          login: activeConnection.login,
          profileName: activeConnection.profileName,
          projectPath: null,
        }
      : { kind: "none" };
  const connectedProviders = useMemo(() => {
    const ids = new Set<ProviderId>();
    for (const connection of connections) {
      ids.add(connection.provider);
    }
    if (isFixtureMode) {
      ids.add("fixtures");
    }
    return [...ids];
  }, [connections, isFixtureMode]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (url.searchParams.get("oauth") !== "complete") return;
    if (!isFreshPendingAuthResult()) {
      recordAuthTrace("connection.pending_oauth.drop_stale_effect", {
        search: window.location.search,
      });
      sessionStorage.removeItem(PENDING_AUTH_RESULT_KEY);
      return;
    }
    const raw = sessionStorage.getItem(PENDING_AUTH_RESULT_KEY);
    if (!raw) return;

    try {
      const { provider, connectionId, linkStatus, linkedProvider } = JSON.parse(raw) as {
        provider: ProviderId;
        connectionId: string;
        linkStatus?: "completed" | "invalid";
        linkedProvider?: string;
      };
      // URL + sessionStorage are external handoff state; this effect imports
      // their one-time snapshot into the connection-store transaction.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPendingOAuthResult({ provider, connectionId });
      setSelectedConnectionId(connectionId);
      setActiveConnectionId(connectionId);
      setFixtureMode(false);
      observeClientCredentialRecovery({
        provider,
        connectionId,
        reason: "oauth_completed",
      });
      recordAuthTrace("connection.pending_oauth.restore", {
        provider,
        connectionId: Boolean(connectionId),
      });
      url.searchParams.delete("oauth");
      window.history.replaceState(null, "", url.toString());
      setOauthCompletionInFlight(true);
      const linkedProviderLabel =
        linkedProvider === "github" ? "GitHub" : linkedProvider === "gitlab" ? "GitLab" : null;

      const hasStoredProject = Boolean(
        sessionStorage.getItem(`dc_project_path_connection:${connectionId}`)
      );

      void Promise.allSettled([refetchSessionContext(), refetchMe(), refetchConnections()])
        .then(([sessionOutcome]) => {
          if (sessionOutcome.status !== "fulfilled") return;
          const sessionResult = sessionOutcome.value;
          if (sessionResult.data?.sessionContext?.authenticated !== true) {
            recordAuthTrace("connection.pending_oauth.session_not_authenticated", {
              provider,
              connectionId: Boolean(connectionId),
            });
            setPendingOAuthResult(null);
            sessionStorage.removeItem(PENDING_AUTH_RESULT_KEY);
            return;
          }

          if (linkStatus === "invalid") {
            toast.warning({
              title: "Provider not linked",
              description: linkedProviderLabel
                ? `${linkedProviderLabel} could not be added to your account. Try Add provider again from Settings.`
                : "The provider could not be added to your account. Try Add provider again from Settings.",
              dedupeKey: `oauth-link-invalid:${provider}:${linkedProvider ?? "unknown"}`,
            });
            return;
          }

          toast.success({
            title:
              linkedProviderLabel && linkStatus === "completed" ? "Provider linked" : "Signed in",
            description:
              linkedProviderLabel && linkStatus === "completed"
                ? `${linkedProviderLabel} added to your account.`
                : hasStoredProject
                  ? undefined
                  : "Select a project to start comparing deploys.",
            dedupeKey:
              linkedProviderLabel && linkStatus === "completed"
                ? `oauth-linked:${provider}:${linkedProvider}`
                : `oauth-connected:${provider}`,
          });
        })
        .finally(() => setOauthCompletionInFlight(false));
    } catch (error) {
      console.error("[connection-store] failed to restore OAuth redirect state", error);
      recordAuthTrace("connection.pending_oauth.invalid_payload", {});
      toast.error({
        title: "DeployCompare could not complete sign-in",
        description: "The OAuth redirect payload was invalid. Start the connection flow again.",
        dedupeKey: "oauth-redirect-invalid",
        durationMs: 0,
      });
      sessionStorage.removeItem(PENDING_AUTH_RESULT_KEY);
    }
  }, [refetchConnections, refetchMe, refetchSessionContext]);

  // Phase 60.1.1 AUTHFIX-02: tracks the LATEST user.id (or null) at all times,
  // so callbacks like switchConnection can read the up-to-date value without
  // rebuilding when user hydrates. previousUserIdRef below only tracks the
  // most-recently-authenticated id and is used for sign-out cleanup; the two
  // have different semantics.
  const latestUserIdRef = useRef<string | null>(user?.id ?? null);
  useEffect(() => {
    latestUserIdRef.current = user?.id ?? null;
  }, [user?.id]);

  const previousUserIdRef = useRef<string | null>(null);

  // Mirror the RESOLVED connection id into the tab-scoped storage key. One
  // direction only (resolution → storage): storage is read only at boot seed
  // time and by the debug pages, never treated as an authority that can
  // disagree with the live resolution. Never writes null — clearing is an
  // explicit act (sign-out / switch-to-fixtures).
  useEffect(() => {
    if (isFixtureMode || !activeConnection) return;
    if (getActiveConnectionId() !== activeConnection.id) {
      setActiveConnectionId(activeConnection.id);
    }
  }, [activeConnection, isFixtureMode]);

  // Sign-out cleanup: only when the session has RESOLVED unauthenticated.
  // Gating on !sessionLoading is what makes transient session refetches
  // (resetStore, auth-debug probes) safe — the old reconcile effect needed
  // per-case guards for exactly those windows.
  useEffect(() => {
    if (isFixtureMode || isSessionAuthenticated || sessionLoading) return;
    if (pendingOAuthResult) return;
    clearClientCredentialObservations();
    recordAuthTrace("connection.reconcile.clear_unauthenticated", { sessionLoading });
    // A resolved sign-out invalidates the tab-local selection transaction.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedConnectionId(null);
    setActiveConnectionId(null);
  }, [isFixtureMode, isSessionAuthenticated, pendingOAuthResult, sessionLoading]);

  // A completed account connection-list read is authoritative for membership.
  // Prune observations for deleted connections, but never during a loading gap.
  useEffect(() => {
    if (isFixtureMode || useTestConnection || connectionsLoading || !connectionsData) return;
    retainClientCredentialObservations(new Set(connections.map((connection) => connection.id)));
  }, [connections, connectionsData, connectionsLoading, isFixtureMode, useTestConnection]);

  // OAuth handoff cleanup: once the just-connected connection appears in the
  // live list and resolves active, the pending marker has done its job.
  useEffect(() => {
    if (!pendingOAuthResult || !user || !activeConnection) return;
    if (pendingOAuthResult.connectionId !== activeConnection.id) return;
    recordAuthTrace("connection.reconcile.active_connection", {
      provider: activeConnection.provider,
      activeConnectionId: Boolean(activeConnection.id),
      connections: connections.length,
    });
    setDefaultConnectionIdForUser(user.id, activeConnection.id);
    // The live connection list has acknowledged the external OAuth handoff.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPendingOAuthResult(null);
    sessionStorage.removeItem(PENDING_AUTH_RESULT_KEY);
  }, [activeConnection, connections.length, pendingOAuthResult, user]);

  useEffect(() => {
    if (user?.id) {
      previousUserIdRef.current = user.id;
      return;
    }
    // Phase 60.1.1: same guard as the reconciliation effect — don't
    // clear the default-connection mapping during a transient session
    // refetch. The me query is gated on isSessionAuthenticated, so it
    // also goes undefined during the gap; treating that as a sign-out
    // wipes the default and breaks post-refetch fallback resolution.
    if (sessionLoading && previousUserIdRef.current) {
      return;
    }
    if (!isSessionAuthenticated && previousUserIdRef.current) {
      clearDefaultConnectionIdForUser(previousUserIdRef.current);
      previousUserIdRef.current = null;
    }
  }, [isSessionAuthenticated, sessionLoading, user?.id]);

  const switchConnection = useCallback(
    (connectionId: string) => {
      const target = connections.find((connection) => connection.id === connectionId);
      if (!target) return;
      setPendingOAuthResult(null);
      sessionStorage.removeItem(PENDING_AUTH_RESULT_KEY);
      setSelectedConnectionId(target.id);
      setActiveConnectionId(target.id);
      setFixtureMode(false);
      // Phase 60.1.1 AUTHFIX-02: read user.id from the latest-value ref instead
      // of the closed-over `user`. The closure can be stale (created before the
      // me-query hydrated user); the ref always has the latest value.
      const userId = latestUserIdRef.current;
      if (userId) {
        setDefaultConnectionIdForUser(userId, target.id);
      }
    },
    // user removed from deps — switchConnection now reads the latest user.id
    // from latestUserIdRef.current instead of the closed-over `user`. The ref
    // is a stable reference and does not need to appear in the dep array.

    [connections]
  );

  const switchProvider = useCallback(
    (provider: ProviderId) => {
      if (provider === "fixtures") {
        setSelectedConnectionId(null);
        setActiveConnectionId(null);
        setFixtureMode(true);
        return;
      }

      const target = connections.find((connection) => connection.provider === provider);
      if (!target) return;
      switchConnection(target.id);
    },
    [connections, switchConnection]
  );

  const switchFixtureView = useCallback(() => {
    switchProvider("fixtures");
  }, [switchProvider]);

  const clearActiveConnection = useCallback(() => {
    setSelectedConnectionId(null);
    setActiveConnectionId(null);
  }, []);

  const clearActiveProvider = useCallback(() => {
    setSelectedConnectionId(null);
    setActiveConnectionId(null);
    setFixtureMode(false);
  }, []);

  const refetchSession = useCallback(async () => {
    const result = await refetchSessionContext();
    if (result.data?.sessionContext?.authenticated) {
      await Promise.all([refetchMe(), refetchConnections()]);
    }
  }, [refetchConnections, refetchMe, refetchSessionContext]);

  const hasConnections = isFixtureMode || connections.length > 0;
  const isAuthenticated = isFixtureMode || isSessionAuthenticated || useTestConnection;
  const accountLoadError = Boolean(
    !isFixtureMode && !useTestConnection && isSessionAuthenticated && (meError || connectionsError)
  );
  const isLoading =
    sessionLoading ||
    pendingOAuthResult !== null ||
    oauthCompletionInFlight ||
    (!isFixtureMode &&
      !useTestConnection &&
      isSessionAuthenticated &&
      (meLoading || connectionsLoading));
  return {
    state: {
      user,
      connections,
      viewContext,
      activeConnectionId,
      activeConnection,
      provider,
      connectedProviders,
      instanceUrl: activeConnection?.instanceUrl ?? session?.instanceUrl ?? "",
      isAuthenticated,
      hasRealSession: isSessionAuthenticated,
      isLoading,
      needsReconnect,
      clientCredentialObservations,
      availableProviders,
      providersLoading,
      debugAccess: session?.debugAccess ?? {
        enabled: false,
        authDebug: false,
        ddbCacheDebug: false,
        comparePerformanceDebug: false,
        compareStreamDebug: false,
        designSystem: false,
        developerTools: false,
      },
      hasConnections,
      isZeroConnectionState:
        isAuthenticated &&
        !isFixtureMode &&
        !isLoading &&
        !accountLoadError &&
        connections.length === 0,
      accountLoadError,
      connectionStatus: activeConnection?.status ?? "disconnected",
      connectionUserAction: activeConnection?.userActionRequired ?? "none",
    },
    actions: {
      switchViewConnection: switchConnection,
      switchFixtureView,
      switchProvider,
      switchConnection,
      clearActiveProvider,
      clearActiveConnection,
      refetchSession,
    },
  };
}
